import { FormHelperText, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContentText from '@mui/material/DialogContentText'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import moment from 'moment/moment'
import * as React from 'react'
import { useEffect, useLayoutEffect } from 'react'
import * as yup from 'yup'
import { useInvalidateCollaborationAppMeetingQuery } from '../../api/hooks/useInvalidateCollaborationAppMeetingQuery'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { ProjectSelect } from '../../components/ProjectSelect'
import { usePackageSubscription } from '../../hooks/usePackageSubscription'
import { usePersonalDiscount } from '../../hooks/usePersonalDiscount'
import { useAssistantAdministrationStore } from '../../store/storeAssistantAdministration'
import { useWrapperStore } from '../../store/storeWrapper'
import { theme } from '../../theme'

const validationSchema = yup.object({
    title: yup.string().trim().required('Пожалуйста, укажите название встречи'),
    source: yup
        .string()
        .matches(
            /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/,
            'Ссылка некорректна',
        )
        .required('Пожалуйста, введите ссылку на встречу'),
})

const yesterday = dayjs().subtract(-1, 'year')

type Props = {
    open: boolean
    handleClose: VoidFunction
    initialDate?: Date
}

export const MeetCreateDialog: React.FC<Props> = ({ open, handleClose, initialDate }) => {
    const [startAt, setStartAt] = React.useState(initialDate ? dayjs(initialDate) : null)
    const [error, setError] = React.useState(false)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { projectListActive } = useWrapperStore((state) => state)

    const { promotePersonalDiscount } = usePersonalDiscount()
    const { promoteUpdatePackageSubscription } = usePackageSubscription()

    const {
        createMeeting,
        errorCreateMeeting,
        isLoadingCreateMeeting,
        isCreateMeetingDone,
        errorUploadFile,
        resetCreateMeetingError,
    } = useAssistantAdministrationStore((state) => state)
    const { isOpenDialogAddBotToMeetNow } = useWrapperStore((state) => state)

    const invalidateMeetings = useInvalidateCollaborationAppMeetingQuery()

    const handleCloseDialog = () => {
        formik.resetForm()
        setStartAt(null)
        handleClose()
    }

    useEffect(() => {
        if (isCreateMeetingDone) {
            invalidateMeetings()
            handleCloseDialog()
        }
    }, [isCreateMeetingDone])

    const formik = useFormik({
        initialValues: {
            source: '',
            title: '',
            isNow: isOpenDialogAddBotToMeetNow ? true : false,
            workplaceId: projectListActive?.[0].id,
        },
        validationSchema,
        onSubmit: async (values) => {
            const { source, isNow, workplaceId, title } = values

            promotePersonalDiscount()
            promoteUpdatePackageSubscription()

            await createMeeting({
                workplace_id: workplaceId,
                source,
                title,
                start_at: isNow ? null : moment(startAt.$d).toISOString(),
            })
        },
    })

    useEffect(() => {
        if (formik.values.isNow) {
            setStartAt(null)
        }
    }, [formik.values])

    useLayoutEffect(() => {
        resetCreateMeetingError()
        setStartAt(initialDate ? dayjs(initialDate) : null)
    }, [open])

    return (
        <ModalDialogWrapper
            open={open}
            sx={{
                '.MuiPaper-root': {
                    width: '560px',
                    maxWidth: '560px',
                },

                [theme.breakpoints.down('md')]: {
                    '.MuiPaper-root': {
                        m: 0,
                        borderRadius: 0,
                        height: '100%',
                        maxHeight: 'unset',
                    },
                },
            }}
            footerActions={
                <>
                    <Button
                        fullWidth
                        onClick={handleCloseDialog}
                        variant="outlined"
                        color="primary">
                        Отменить
                    </Button>
                    <Button
                        fullWidth
                        onClick={() => {
                            formik.handleSubmit()
                        }}
                        variant="contained"
                        disabled={
                            !(formik.isValid && formik.dirty) ||
                            error ||
                            (!formik.values.isNow && !startAt) ||
                            isLoadingCreateMeeting
                        }>
                        Добавить
                    </Button>
                </>
            }
            handleClose={handleCloseDialog}
            title="Подключить бота к встрече">
            <DialogContentText>
                {isLoadingCreateMeeting && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
                {errorCreateMeeting && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography color="error">{errorCreateMeeting}</Typography>
                    </Box>
                )}

                <FormControl
                    fullWidth
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                    }}>
                    <Typography variant="caption2" sx={{ mr: 2 }}>
                        Проект
                    </Typography>
                    <ProjectSelect
                        id={formik.values.workplaceId}
                        handleChange={(name, value) => formik.setFieldValue(name, value)}
                    />
                </FormControl>
                <TextField
                    sx={{ marginTop: 4, pb: 1 }}
                    margin="dense"
                    id="title"
                    name="title"
                    label="Название встречи"
                    placeholder="Введите название встречи"
                    fullWidth
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    required
                />
                <TextField
                    sx={{ marginTop: 4, pb: 1 }}
                    margin="dense"
                    id="source"
                    name="source"
                    label="Ссылка на собрание"
                    placeholder="https://"
                    fullWidth
                    value={formik.values.source}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.source && Boolean(formik.errors.source)}
                    helperText={formik.touched.source && formik.errors.source}
                    required
                />

                <FormControlLabel
                    sx={{ mb: 1 }}
                    id="isNow"
                    name="isNow"
                    control={
                        <Checkbox checked={formik.values.isNow} onClick={() => setStartAt(null)} />
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Подключить бота сейчас"
                />
                <DemoContainer sx={{ paddingTop: '20px' }} components={['DateTimePicker']}>
                    <DateTimePicker
                        slotProps={{
                            textField: {
                                sx: {
                                    '&>label': {
                                        color: formik.values.isNow ? '#9e9e9e !important' : '',
                                    },
                                },
                                required: true,
                            },
                            tabs: {
                                sx: {
                                    padding: '16px',
                                },
                            },
                        }}
                        onError={(newError) => setError(newError)}
                        disabled={formik.values.isNow}
                        dayOfWeekFormatter={(weekday) => `${weekday.format('dd')}.`}
                        format="DD.MM.YYYY - HH:mm"
                        value={startAt}
                        onChange={(value) => {
                            setStartAt(value)
                        }}
                        label="Дата старта собрания"
                        viewRenderers={
                            !isMobile
                                ? {
                                      hours: null,
                                      minutes: null,
                                      seconds: null,
                                  }
                                : {
                                      hours: renderTimeViewClock,
                                      minutes: renderTimeViewClock,
                                      seconds: renderTimeViewClock,
                                  }
                        }
                        ampm={false}
                        closeOnSelect={false}
                        disablePast={true}
                        maxDate={yesterday}
                    />
                </DemoContainer>
                <FormHelperText>
                    Бот подключится к вашей встрече в Google Meet или Яндекс Телемост, запишет ее и
                    по окончанию расшифрует. Бота можно отключить в любой момент
                </FormHelperText>
            </DialogContentText>
        </ModalDialogWrapper>
    )
}
